import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Header, Footer, Page } from "@/components/Shell";
import { API } from "@/lib/brand";

const POLL_INTERVAL_MS = 3000;
const MAX_POLLS = 40; // ~2 minutes

const STATUS_LABELS = {
  pending: "EN COURS",
  initiated: "EN COURS",
  paid: "CONFIRMÉ",
  failed: "ÉCHOUÉ",
  expired: "EXPIRÉ",
  refunded: "REMBOURSÉ",
};

export default function PaymentConfirmed() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id") || "";
  const nav = useNavigate();
  const [state, setState] = useState({
    payment_status: "pending",
    status: "initiated",
    revelateur_token: "",
    report_ready: false,
    error: null,
  });
  const [polls, setPolls] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!sessionId) {
      setState((s) => ({ ...s, error: "Session de paiement manquante." }));
      return;
    }
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (!alive) return;
        setState((s) => ({ ...s, ...data, error: null }));
        if (data.report_ready && data.revelateur_token) {
          setTimeout(() => nav(`/rapport/${data.revelateur_token}`, { replace: true }), 900);
          return true;
        }
        if (data.payment_status === "paid" && data.revelateur_token) {
          // Paiement confirmé, rapport en cours de génération — on continue à sonder brièvement
          return false;
        }
        if (["failed", "expired", "refunded"].includes(data.payment_status)) {
          return true;
        }
        return false;
      } catch (err) {
        console.warn("[PaymentConfirmed] status poll failed", err?.message || err);
        return false;
      }
    };
    (async () => {
      const done = await tick();
      if (done) return;
      timerRef.current = setInterval(async () => {
        setPolls((p) => p + 1);
        const d = await tick();
        if (d) clearInterval(timerRef.current);
      }, POLL_INTERVAL_MS);
    })();
    return () => { alive = false; if (timerRef.current) clearInterval(timerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const timeout = polls >= MAX_POLLS;
  const failed = ["failed", "expired"].includes(state.payment_status);
  const paidNoReport = state.payment_status === "paid" && !state.report_ready;

  return (
    <Page tone="ivory">
      <Header variant="ivory" />
      <section className="max-w-3xl mx-auto px-6 md:px-10 pt-20 pb-24" data-testid="payment-confirmed-page">
        <div className="overline mb-6">Confirmation de paiement</div>
        <h1 className="font-display text-[38px] md:text-[48px] leading-tight text-ink">
          {state.report_ready ? "Votre lecture stratégique est prête." :
            failed ? "Le paiement n'a pas pu être finalisé." :
            "Votre paiement a bien été reçu."}
        </h1>

        <div className="mt-10 border border-[color:var(--border-bronze)] bg-stone rounded-sm p-8" data-testid="payment-confirmed-card">
          {failed ? (
            <>
              <div className="overline mb-4 text-bronze-dark">Paiement non finalisé</div>
              <p className="text-[15px] text-text-2 leading-relaxed">
                {state.payment_status === "expired"
                  ? "La session de paiement a expiré avant sa confirmation."
                  : "Le traitement du paiement a échoué. Aucun montant n'a été prélevé."}
              </p>
              <p className="mt-4 text-[13px] text-text-2/80">
                Vous pouvez recommencer sans perdre vos réponses. Si vous préférez un accompagnement, écrivez à
                <a className="underline mx-1 text-bronze-dark hover:text-ink" href="mailto:direction@maisonbeaudette.com">direction@maisonbeaudette.com</a>.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                {state.revelateur_token && (
                  <Link to={`/paiement/${state.revelateur_token}`} className="btn-primary" data-testid="btn-retry-payment">
                    Reprendre le paiement
                  </Link>
                )}
                <Link to="/retrouver" className="btn-ghost" data-testid="btn-retrieve-alt">
                  Accéder à ma lecture plus tard
                </Link>
              </div>
            </>
          ) : state.report_ready ? (
            <>
              <div className="overline mb-4">Paiement confirmé</div>
              <h2 className="font-display text-[26px] text-ink">Votre lecture est prête.</h2>
              <p className="mt-4 text-text-2 leading-relaxed">
                Nous vous redirigeons vers votre Rapport de Révélation Stratégique™.
              </p>
              <div className="mt-6">
                {state.revelateur_token && (
                  <Link to={`/rapport/${state.revelateur_token}`} className="btn-primary" data-testid="btn-open-report">
                    Accéder à ma lecture
                  </Link>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center gap-4">
                <span className="inline-block h-2 w-2 rounded-full bg-bronze animate-pulse" aria-hidden />
                <div className="overline">Préparation de votre accès</div>
              </div>
              <p className="mt-6 text-[16px] text-text-2 leading-relaxed">
                Maison Beaudette prépare votre accès. Cela prend généralement entre 30 secondes et 3 minutes.
                Cette page se met à jour automatiquement — vous serez redirigé dès que votre lecture est prête.
              </p>
              <p className="mt-4 text-[13px] text-text-2/80">
                Un lien d'accès privé vous sera également envoyé à l'adresse courriel utilisée lors du paiement.
              </p>
              <div className="mt-6 text-[11px] tracking-[0.24em] uppercase text-text-2">
                Statut&nbsp;: <span className="text-ink">{STATUS_LABELS[state.payment_status] || "EN COURS"}</span>
                {paidNoReport && <> · <span className="text-bronze-dark">Rédaction du rapport en cours</span></>}
              </div>
              {timeout && (
                <div className="mt-6 text-[14px] text-bronze-dark border-t border-[color:var(--border-bronze)] pt-5">
                  La confirmation prend un peu plus de temps que d'habitude. Vérifiez votre boîte courriel
                  (dossier indésirables inclus) ou écrivez-nous à
                  <a className="underline mx-1" href="mailto:direction@maisonbeaudette.com">direction@maisonbeaudette.com</a>
                  — nous vous répondrons rapidement.
                </div>
              )}
              <div className="mt-8 flex flex-wrap gap-4">
                <Link to="/retrouver" className="btn-ghost" data-testid="btn-retrieve-alt">
                  Accéder à ma lecture plus tard
                </Link>
              </div>
            </>
          )}
        </div>

        <p className="mt-8 text-[12px] tracking-[0.26em] uppercase text-text-2">
          Paiement sécurisé · Confirmation automatique · Confidentialité assurée
        </p>
      </section>
      <Footer />
    </Page>
  );
}
